/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import {
  CpStageCcDto,
  CpStageDto,
  CpUnusedCcDto,
  CriticalPathDetailsDto,
  CriticalPathSaveDto,
  JobContractStartBasedOnType,
} from '@gh/builder-shared-data';
import {
  createArrayHelper,
  entityComplementor,
  EntityConfig,
  entityValidatorByEntityConfig,
  fieldStateByEntityConfig,
  fieldValidatorByEntityConfig,
  setEntityFields,
} from '@gh/common-shared';
import { ID, WithEntityState, WithVersion } from '@gh/common-shared-data';
import { FocusedCell } from '@gh/core-ui-grid';
import { maybeMap } from '@gh/core-util';

import { first, flow, isNil, map, omit, sortBy, uniqueId } from 'lodash';

export type CpStage = WithEntityState<WithVersion<{
  criticalPathId?: ID;
  name: string;
  isSlab: boolean;
  substituteId?: ID;
  costCenters: CpStageCc[];
  // if this is new stage added during this edit session
  isNew: boolean;
  // if this stage should be removed after this edit session
  isDeleted: boolean;
  // if this stage is active or marked as deleted
  isActive: boolean;
}>>;
export type CpUnusedCc = CpUnusedCcDto;
export type CpStageCc = WithEntityState<CpStageCcDto & { isActive: boolean; isNew: boolean }>;

export type CriticalPathDetails = {
  contractStartBasedOn: JobContractStartBasedOnType;
  stages: CpStage[];
  unusedCostCenters: CpUnusedCc[];
  selection: CriticalPathSelection;
  affectedDrawRules?: string[];
  warningMessage?: string;
};

export type CriticalPathSelection = {
  selectedStageId?: ID;
  showDeletedStages: boolean;
  focusedCell?: FocusedCell;
};

export const CRITICAL_PATH_DETAILS_INIT: CriticalPathDetails = {
  contractStartBasedOn: JobContractStartBasedOnType.ScheduleStart,
  stages: [],
  unusedCostCenters: [],
  selection: { showDeletedStages: false },
};

export const getCpStageId = (stage: CpStage) => stage.criticalPathId;
export const getCpStageCcId = (cc: CpStageCc) => cc.costCenterId;
export const getCpUnusedCcId = (cc: CpUnusedCc) => cc.costCenterId;

const CP_STAGE_ENTITY_CONFIG: EntityConfig<CpStage> = {
  isSlab: { editable: (stage) => stage.isActive },
  substituteId: {
    required: (stage) => !stage.isActive,
    editable: (stage) => !stage.isActive,
  },
};

const CP_STAGE_CC_ENTITY_CONFIG: EntityConfig<CpStageCc> = {
  isContractStart: { editable: (cc) => cc.isActive },
};

export const validateCpStageField = fieldValidatorByEntityConfig(CP_STAGE_ENTITY_CONFIG);
export const validateCpStage = entityValidatorByEntityConfig(CP_STAGE_ENTITY_CONFIG);
export const complementCpStage = entityComplementor(validateCpStage);
export const cpStageFieldState = fieldStateByEntityConfig(CP_STAGE_ENTITY_CONFIG);

export const setCpStageFields = flow(setEntityFields, complementCpStage);

export const cpStageCcFieldState = fieldStateByEntityConfig(CP_STAGE_CC_ENTITY_CONFIG);

export const cpStageArr = createArrayHelper(getCpStageId);
export const cpStageCcArr = createArrayHelper(getCpStageCcId);
export const cpUnusedCcArr = createArrayHelper(getCpUnusedCcId);

const criticalPathStageFromDto = (dto: CpStageDto): CpStage => ({
  ...dto,
  isNew: false,
  isActive: isNil(dto.substituteId),
  isDeleted: false,
  costCenters: map(dto.costCenters, (cc) => ({ ...cc, isActive: true, isNew: false })),
});

const criticalPathStageToSaveDto = (dto: CpStage): CpStageDto => ({
  ...<CpStageDto>omit(dto, ['isNew', 'isActive', 'criticalPathId', 'entityState']),
  criticalPathId: dto.isNew ? void 0 : dto.criticalPathId,
  costCenters: dto.costCenters.map((cc) => <CpStageCcDto>({
    ...omit(cc, 'isActive', 'isNew'),
    criticalPathId: cc.isNew ? void 0 : cc.criticalPathId,
  })),
});

export const criticalPathDetailsToSaveDto = (model: CriticalPathDetails): CriticalPathSaveDto => ({
  criticalPaths: model.stages.map(criticalPathStageToSaveDto),
});

export const criticalPathDetailsFromDto = (dto: CriticalPathDetailsDto): CriticalPathDetails => {
  const stages = dto.criticalPaths.map(criticalPathStageFromDto);
  return {
    contractStartBasedOn: dto.contractStartBasedOn,
    stages,
    unusedCostCenters: sortBy(dto.jobScheduleCostCenters, 'code'),
    selection: { showDeletedStages: false, selectedStageId: maybeMap(first(stages), getCpStageId) },
    affectedDrawRules: dto.affectedDrawRules,
    warningMessage: dto.warningMessage,
  };
};

export const newCpStage = (): CpStage => ({
  criticalPathId: uniqueId('cp-stage:'),
  name: '',
  isNew: true,
  isSlab: false,
  isActive: true,
  isDeleted: false,
  costCenters: [],
});

export const cpUnusedCcFromStageCc = (cc: CpStageCc): CpUnusedCc => ({
  costCenterId: cc.costCenterId,
  code: cc.costCenterCode,
  name: cc.costCenterName,
  daysInSchedule: cc.costCenterScheduledDays,
});

export const cpUnusedCcToStageCc = (cc: CpUnusedCc, { isNew, isActive, criticalPathId }: CpStage): CpStageCc => ({
  criticalPathId,
  costCenterId: cc.costCenterId,
  costCenterCode: cc.code,
  costCenterName: cc.name,
  costCenterScheduledDays: cc.daysInSchedule,
  isContractStart: false,
  isActive,
  isNew,
});

export const cpStageToRemoved = (stage: CpStage) => setCpStageFields(stage, {
  isActive: false,
  isSlab: false,
  costCenters: cpStageCcArr.updateAll(stage.costCenters, (cc) => setEntityFields(cc, {
    isActive: false,
    isContractStart: false,
  })),
});

export const cpStageToRestored = (stage: CpStage) => setCpStageFields(stage, {
  isActive: true,
  isSlab: false,
  substituteId: void 0,
  costCenters: cpStageCcArr.updateAll(stage.costCenters, (cc) => setEntityFields(cc, {
    isActive: true,
  })),
});
